import { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { SalaryDeviationFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { RadioGroupProps } from "models/propTypes/wintFormPropsTypes";
import { DeviationDeleteFormProps } from "models/propTypes/wintSalaryPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { RadioGroup, useWintForm, VerticalFormField, WintForm } from "components/common/wintForms";
//#endregion
import useWintSalaryDeviationService from "serviceHooks/useWintSalaryDeviationService";
import { LocalizationHelperInstance } from "utils/helpers";
import useWintDeviationValidation from "./useWintDeviationValidation";
import cssExports from "theme/styles/main.module.scss";

const DeleteForm: ForwardRefRenderFunction<FormHandles, DeviationDeleteFormProps> = (props, ref) => {
  const { selectedPeriod, openedPeriod, onDeleteSuccess, showAllDeleteOption } = props;

  const { deleteDeviationReapprove, deletePartialDeviationReapprove } = useWintSalaryDeviationService();
  const openedPeriodStart = LocalizationHelperInstance.getFormattedDate(openedPeriod?.start ?? new Date());
  const openedPeriodEnd = LocalizationHelperInstance.getFormattedDate(openedPeriod?.end ?? new Date());
  const selectedPeriodStart = LocalizationHelperInstance.getFormattedDate(selectedPeriod?.start ?? new Date());
  const selectedPeriodEnd = LocalizationHelperInstance.getFormattedDate(selectedPeriod?.end ?? new Date());

  const { deleteValidationSchema } = useWintDeviationValidation();

  const selectionItems: SelectionItem[] = [
    ...(openedPeriod
      ? [
          {
            id: "openedPeriod",
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.DeletePeriodOption_OpenedPeriod,
              undefined,
              { startDate: openedPeriodStart, endDate: openedPeriodEnd }
            ),
          },
        ]
      : []),
    ...(selectedPeriod
      ? [
          {
            id: "selectedPeriod",
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.DeletePeriodOption_SelectedPeriod,
              undefined,
              { startDate: selectedPeriodStart, endDate: selectedPeriodEnd }
            ),
          },
        ]
      : []),
    ...(showAllDeleteOption
      ? [
          {
            id: "all",
            value: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.SalaryDeviation.DeletePeriodOption_All
            ),
          },
        ]
      : []),
  ];

  const defaultValues: SalaryDeviationFormTypes.WintDeviationDelete = {
    periodSelection: (selectionItems[0]?.id as SalaryDeviationFormTypes.WintDeviationFormSelection) ?? "openedPeriod",
  };

  const handleSubmit = async (values: SalaryDeviationFormTypes.WintDeviationDelete) => {
    try {
      let result;

      if (values.periodSelection === "all") {
        result = await deleteDeviationReapprove(props.deviationId);
      }

      if (values.periodSelection === "openedPeriod") {
        result = await deletePartialDeviationReapprove(props.deviationId, openedPeriod?.start, openedPeriod?.end);
      }
      if (values.periodSelection === "selectedPeriod") {
        result = await deletePartialDeviationReapprove(props.deviationId, selectedPeriod?.start, selectedPeriod?.end);
      }

      onDeleteSuccess?.(result?.data);
    } catch {
      // Error display is handled globally by the API client interceptor (showErrors: true)
    }
  };

  const { wintFormProps, connect } = useWintForm<SalaryDeviationFormTypes.WintDeviationDelete>({
    formRef: ref,
    defaultValues: defaultValues,
    validationSchema: deleteValidationSchema,
    handleSubmit: handleSubmit,
    enableReinitialize: true,
  });

  return (
    <WintForm {...wintFormProps}>
      <Grid item className={cssExports.formControlLabel} style={{ marginBottom: 4 }}>
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.DeleteDeviationDescription)}
      </Grid>
      <Grid container>
        <Grid item xs={9}>
          <VerticalFormField<RadioGroupProps>
            Component={RadioGroup}
            {...connect("periodSelection")}
            items={selectionItems}
          />
        </Grid>
      </Grid>
    </WintForm>
  );
};

export default forwardRef(DeleteForm);
